import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Container,
  Message,
  Icon,
  Grid,
  Loader,
  Dimmer,
  Header,
  Button,
  Modal,
  Segment,
  Progress,
  Image,
} from "semantic-ui-react";
import LibraryCard from "./ProfileForm/LibraryCard";
import ChangePasswordForm from "./ProfileForm/ChangePasswordForm";
import TabNavigation from "./TabNavigation";
import ProfileTabContent from "./ProfileTabContent";
import { profileApi, ProfileApiError } from "@api/profile/profile";
import { userApi } from "@api/users/user";
import { isFacultyUser, isLibrarianOrAdmin } from "UserRoleUtils";
import { sessionManager } from "@authentication/services/SessionManager";
import { useIsMobile } from "@utils/mobileUtils";

// ==================== CONSTANTS ====================
const COLORS = {
  primary: "#225745",
  secondary: "#457a64",
  background: "#f8f9fa",
  white: "#fff",
  infoBackground: "#e7f3ff",
  infoBorder: "#0066cc",
  text: "#2c3e50",
  textSecondary: "#6c757d",
  textTertiary: "#444",
};

const ADMIN_ROLES = ["admin", "librarian"];

// ==================== UTILITY FUNCTIONS ====================
const getFullImageUrl = (imageUrl) => {
  if (!imageUrl) return null;

  // If it's already a full URL, return as is
  if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
    return imageUrl;
  }

  // Get base URL from environment or current window location
  const baseUrl =
    process.env.REACT_APP_INVENIO_UI_URL ||
    (typeof window !== "undefined" ? window.location.origin : "");

  if (!baseUrl) {
    return imageUrl;
  }

  // Handle different URL formats
  if (imageUrl.startsWith("/api/")) {
    // Already has /api prefix
    return `${baseUrl.replace(/\/$/, "")}${imageUrl}`;
  } else if (imageUrl.startsWith("/")) {
    // Relative path, add /api prefix
    return `${baseUrl.replace(/\/$/, "")}/api${imageUrl}`;
  } else {
    // Relative path without leading slash
    return `${baseUrl.replace(/\/$/, "")}/api/${imageUrl}`;
  }
};

const getUserRoles = (user) =>
  !user ? [] : Array.isArray(user.roles) ? user.roles : [user.roles];

const hasAdminRights = (user) =>
  getUserRoles(user).some((role) =>
    ADMIN_ROLES.includes(role?.toLowerCase?.())
  );

const shouldShowLibraryCard = (user) => {
  if (!user) return false;
  if (hasAdminRights(user)) return false;
  const isFaculty = isFacultyUser(user);
  if (isFaculty) {
    return false;
  }
  return true;
};

// ==================== CUSTOM HOOKS ====================
const useCurrentUser = (userProp) => {
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const getCurrentUser = () => {
      if (sessionManager.user) {
        setCurrentUser(sessionManager.user);
        return;
      }
      if (userProp) setCurrentUser(userProp);
    };

    getCurrentUser();
    const intervalRef = setInterval(() => {
      if (sessionManager.user && !currentUser) {
        setCurrentUser(sessionManager.user);
        clearInterval(intervalRef);
      }
    }, 300);

    return () => clearInterval(intervalRef);
  }, [userProp, currentUser]);

  return [currentUser, setCurrentUser];
};

const useUserData = (userProp, isLoading, onUserUpdate) => {
  const [user, setUser] = useState(userProp || {});
  const [fetchingUserData, setFetchingUserData] = useState(false);

  useEffect(() => {
    if (userProp) {
      setUser((prevUser) => ({
        ...userProp,
        image_url: userProp.image_url || prevUser.image_url,
      }));
    }
  }, [userProp]);

  useEffect(() => {
    const fetchUserData = async () => {
      if ((user?.full_name && user?.email) || fetchingUserData) {
        return;
      }

      setFetchingUserData(true);

      try {
        const userResponse = await userApi.get();
        const userData = userResponse?.data;

        if (userData) {
          setUser((prevUser) => ({
            ...prevUser,
            ...userData,
            image_url: userData.image_url || prevUser.image_url,
          }));

          if (onUserUpdate) {
            onUserUpdate(userData);
          }
        }
      } catch (error) {
        // Silently handle the error
      } finally {
        setFetchingUserData(false);
      }
    };

    if (!isLoading && (!user?.full_name || !user?.email)) {
      fetchUserData();
    }
  }, [isLoading, user?.full_name, user?.email, fetchingUserData, onUserUpdate]);

  return [user, setUser, fetchingUserData];
};

const useImageInfo = (isLoading, user, onUserUpdate) => {
  const isFetchingRef = useRef(false);
  const hasFetchedRef = useRef(false);
  const lastFetchedUserIdRef = useRef(null);

  // Stabilize the callback reference
  const onUserUpdateRef = useRef(onUserUpdate);

  useEffect(() => {
    onUserUpdateRef.current = onUserUpdate;
  }, [onUserUpdate]);

  useEffect(() => {
    // Don't fetch if:
    // 1. Still loading
    // 2. No user ID
    // 3. Already fetching
    // 4. Already fetched for this user
    if (
      isLoading ||
      !user?.id ||
      isFetchingRef.current ||
      (hasFetchedRef.current && lastFetchedUserIdRef.current === user.id)
    ) {
      return;
    }

    const fetchImageInfo = async () => {
      isFetchingRef.current = true;

      try {
        const imageInfo = await profileApi.getImageInfo();

        // Only update if the image URL is different
        if (imageInfo.image_url && imageInfo.image_url !== user.image_url) {
          const updatedUser = { ...user, image_url: imageInfo.image_url };

          if (onUserUpdateRef.current) {
            onUserUpdateRef.current(updatedUser);
          }
        }

        // Mark as successfully fetched for this user
        hasFetchedRef.current = true;
        lastFetchedUserIdRef.current = user.id;
      } catch (error) {
        console.error("Failed to fetch image info:", error);
        // Don't mark as fetched on error, allow retry
      } finally {
        isFetchingRef.current = false;
      }
    };

    fetchImageInfo();
  }, [isLoading, user?.id]); // Removed user.image_url and onUserUpdate from dependencies

  // Reset fetched flag when user changes
  useEffect(() => {
    if (user?.id && lastFetchedUserIdRef.current !== user.id) {
      hasFetchedRef.current = false;
    }
  }, [user?.id]);
};

// ==================== COMPONENTS ====================
const LoadingState = () => (
  <Container fluid className="profile-loading-container">
    <Dimmer active inverted>
      <Loader size="large" inverted>
        Loading Profile...
      </Loader>
    </Dimmer>
    <div style={{ minHeight: "400px" }} />
  </Container>
);

const SectionHeader = ({ title, fontSize = "1.1rem" }) => {
  const isMobile = useIsMobile();
  return (
    <div
      style={{
        backgroundColor: COLORS.primary,
        color: COLORS.white,
        padding: isMobile ? "0.6rem 0.8rem" : "0.8rem 1rem",
        textAlign: "center",
        fontWeight: "bold",
        fontSize: isMobile ? "1rem" : fontSize,
      }}
    >
      {title}
    </div>
  );
};

const UserInfo = ({ user }) => {
  const isMobile = useIsMobile();
  const displayRoles = user?.roles
    ? Array.isArray(user.roles)
      ? user.roles.join(", ")
      : user.roles
    : "";

  return (
    <>
      <Header
        as={isMobile ? "h3" : "h2"}
        style={{
          color: COLORS.text,
          margin: "0 0 0.5rem 0",
          fontSize: isMobile ? "1.3rem" : "1.5rem",
          textAlign: "center",
        }}
      >
        My Profile
      </Header>
      <p
        style={{
          color: COLORS.textSecondary,
          fontSize: isMobile ? "0.85rem" : "0.9rem",
          textAlign: "center",
        }}
      >
        Manage your profile settings and personal information
      </p>

      {user?.full_name && (
        <div style={{ marginTop: "1rem" }}>
          <Header
            as="h4"
            style={{
              color: COLORS.primary,
              margin: "0 0 0.25rem 0",
              textAlign: "center",
              fontSize: isMobile ? "1.1rem" : "1.2rem",
            }}
          >
            Welcome, {user.full_name}
          </Header>
          {user.email && (
            <p
              style={{
                color: COLORS.textSecondary,
                margin: 0,
                fontSize: isMobile ? "0.8rem" : "0.85rem",
                textAlign: "center",
              }}
            >
              {user.email}
              {displayRoles && ` • ${displayRoles}`}
            </p>
          )}
        </div>
      )}
    </>
  );
};

const ProfileAvatar = ({ user, onImageUpdate, onError }) => {
  const [state, setState] = useState({
    isHovered: false,
    showModal: false,
    selectedFile: null,
    previewUrl: null,
    uploadError: null,
    deleteError: null,
    isUploading: false,
    uploadProgress: 0,
    currentImageUrl: user?.image_url,
  });
  const fileInputRef = useRef(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    setState((prev) => ({ ...prev, currentImageUrl: user?.image_url }));
  }, [user?.image_url]);

  const resetModalState = () => {
    setState((prev) => ({
      ...prev,
      selectedFile: null,
      previewUrl: null,
      uploadError: null,
      deleteError: null,
      uploadProgress: 0,
    }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleAvatarClick = () => {
    setState((prev) => ({ ...prev, showModal: true }));
    resetModalState();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validation = profileApi.validateImageFile(file);
    if (!validation.valid) {
      setState((prev) => ({
        ...prev,
        uploadError: validation.errors.join("; "),
      }));
      return;
    }

    setState((prev) => ({ ...prev, selectedFile: file, uploadError: null }));
    const reader = new FileReader();
    reader.onload = (e) =>
      setState((prev) => ({ ...prev, previewUrl: e.target.result }));
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!state.selectedFile) return;
    setState((prev) => ({
      ...prev,
      isUploading: true,
      uploadError: null,
      uploadProgress: 0,
    }));

    try {
      const response = await profileApi.uploadImage(
        state.selectedFile,
        (progress) =>
          setState((prev) => ({ ...prev, uploadProgress: progress }))
      );

      if (response.success) {
        setState((prev) => ({
          ...prev,
          currentImageUrl: response.image_url,
          showModal: false,
        }));
        if (onImageUpdate) {
          onImageUpdate(response.image_url);
        }
        resetModalState();
      } else {
        throw new Error(response.error || "Upload failed");
      }
    } catch (error) {
      const errorMessage =
        error instanceof ProfileApiError
          ? error.message
          : "Upload failed. Please try again.";
      setState((prev) => ({ ...prev, uploadError: errorMessage }));
      if (error instanceof ProfileApiError && onError) {
        onError({ type: "upload", code: error.code, message: error.message });
      }
    } finally {
      setState((prev) => ({ ...prev, isUploading: false }));
    }
  };

  const handleDelete = async () => {
    setState((prev) => ({ ...prev, isUploading: true, deleteError: null }));

    try {
      const response = await profileApi.deleteImage();
      if (response.success) {
        setState((prev) => ({
          ...prev,
          currentImageUrl: null,
          showModal: false,
        }));
        if (onImageUpdate) {
          onImageUpdate(null);
        }
      } else {
        throw new Error(response.error || "Delete failed");
      }
    } catch (error) {
      const errorMessage =
        error instanceof ProfileApiError
          ? error.message
          : "Delete failed. Please try again.";
      setState((prev) => ({ ...prev, deleteError: errorMessage }));
      if (error instanceof ProfileApiError && onError) {
        onError({ type: "delete", code: error.code, message: error.message });
      }
    } finally {
      setState((prev) => ({ ...prev, isUploading: false }));
    }
  };

  const avatarStyle = useMemo(
    () => ({
      width: isMobile ? "100px" : "120px",
      height: isMobile ? "100px" : "120px",
      borderRadius: "50%",
      backgroundColor: COLORS.secondary,
      margin: "0 auto 1rem auto",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: `2px solid ${COLORS.secondary}`,
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      position: "relative",
      overflow: "hidden",
      cursor: "pointer",
    }),
    [isMobile]
  );

  const overlayStyle = useMemo(
    () => ({
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      opacity: state.isHovered ? 1 : 0,
      transition: "opacity 0.3s",
    }),
    [state.isHovered]
  );

  return (
    <div style={{ textAlign: "center", marginBottom: "2rem" }}>
      <div
        style={avatarStyle}
        onMouseEnter={() => setState((prev) => ({ ...prev, isHovered: true }))}
        onMouseLeave={() => setState((prev) => ({ ...prev, isHovered: false }))}
        onClick={handleAvatarClick}
      >
        {state.currentImageUrl ? (
          <img
            src={getFullImageUrl(state.currentImageUrl)}
            alt="Profile"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={(e) => {
              console.error("Image failed to load:", e.target.src);
              setState((prev) => ({ ...prev, currentImageUrl: null }));
            }}
          />
        ) : (
          <Icon
            name="user"
            size={isMobile ? "big" : "huge"}
            style={{ color: COLORS.white, margin: 0 }}
          />
        )}
        <div style={overlayStyle}>
          <Icon name="camera" size="large" style={{ color: COLORS.white }} />
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "center",
          gap: isMobile ? "0.5rem" : "0",
        }}
      >
        <Button
          size={isMobile ? "tiny" : "small"}
          basic
          color="blue"
          onClick={handleAvatarClick}
          loading={state.isUploading}
          disabled={state.isUploading}
          fluid={isMobile}
        >
          <Icon name="upload" />
          {state.currentImageUrl ? "Change Picture" : "Upload Picture"}
        </Button>

        {state.currentImageUrl && (
          <Button
            size={isMobile ? "tiny" : "small"}
            basic
            color="red"
            style={{ marginLeft: isMobile ? "0" : "0.5rem" }}
            onClick={handleDelete}
            loading={state.isUploading}
            disabled={state.isUploading}
            fluid={isMobile}
          >
            <Icon name="trash" />
            Delete
          </Button>
        )}
      </div>

      <Modal
        open={state.showModal}
        onClose={() => setState((prev) => ({ ...prev, showModal: false }))}
        size="small"
        closeIcon
      >
        <Modal.Header>
          {state.currentImageUrl
            ? "Change Profile Picture"
            : "Upload Profile Picture"}
        </Modal.Header>
        <Modal.Content>
          {state.uploadError && (
            <Message negative style={{ marginBottom: "1rem" }}>
              <Message.Header>Upload Error</Message.Header>
              <p>{state.uploadError}</p>
            </Message>
          )}

          {state.deleteError && (
            <Message negative style={{ marginBottom: "1rem" }}>
              <Message.Header>Delete Error</Message.Header>
              <p>{state.deleteError}</p>
            </Message>
          )}

          {state.isUploading && (
            <Segment style={{ marginBottom: "1rem" }}>
              <div style={{ textAlign: "center", marginBottom: "1rem" }}>
                <Icon name="cloud upload" size="large" color="blue" />
                <p>
                  <strong>Uploading...</strong>
                </p>
              </div>
              <Progress
                percent={state.uploadProgress}
                indicating
                progress
                color="blue"
              />
            </Segment>
          )}

          <div style={{ textAlign: "center" }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />

            {!state.selectedFile ? (
              <div>
                <Icon name="cloud upload" size="huge" color="grey" />
                <p>Select an image file to upload (Max 5MB)</p>
                <p style={{ fontSize: "0.9em", color: COLORS.textSecondary }}>
                  Supported formats: PNG, JPG, JPEG, GIF, WebP
                </p>
                <Button primary onClick={() => fileInputRef.current?.click()}>
                  Choose File
                </Button>
              </div>
            ) : (
              <div style={{ textAlign: "center", margin: "1rem 0" }}>
                <p>
                  <strong>Selected file:</strong> {state.selectedFile.name}
                </p>
                <p style={{ fontSize: "0.9em", color: COLORS.textSecondary }}>
                  Size: {(state.selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
                {state.previewUrl && (
                  <Image
                    src={state.previewUrl}
                    centered
                    style={{
                      maxWidth: "200px",
                      maxHeight: "200px",
                      borderRadius: "8px",
                      border: `2px solid ${COLORS.primary}`,
                    }}
                  />
                )}
                <div style={{ marginTop: "1rem" }}>
                  <Button onClick={() => fileInputRef.current?.click()} basic>
                    Choose Different File
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => setState((prev) => ({ ...prev, showModal: false }))}
            disabled={state.isUploading}
          >
            Cancel
          </Button>
          <Button
            primary
            onClick={handleUpload}
            disabled={!state.selectedFile || state.isUploading}
            loading={state.isUploading}
          >
            Upload
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
const ProfileContainer = ({
  profile,
  user: userProp,
  isLoading,
  hasError,
  errorMessage,
  hasSuccess,
  successMessage,
  onSubmit,
  onPasswordChanged,
  onImageUpload,
  onUserUpdate,
}) => {
  const [activeTab, setActiveTab] = useState("profile");
  const [imageErrors, setImageErrors] = useState([]);
  const isMobile = useIsMobile();

  // Custom hooks
  const [currentUser, setCurrentUser] = useCurrentUser(userProp);
  const [user, setUser, fetchingUserData] = useUserData(
    userProp,
    isLoading,
    onUserUpdate
  );
  useImageInfo(isLoading, user, (updatedUser) => {
    setUser(updatedUser);
    if (onUserUpdate) onUserUpdate(updatedUser);
  });

  // Derived state
  const effectiveUser = currentUser || user;
  const isLibrarianAdminOrFaculty = Boolean(
    effectiveUser?.roles && isLibrarianOrAdmin(effectiveUser)
  );
  const showLibraryCardTab = shouldShowLibraryCard(effectiveUser);

  useEffect(() => {
    if (activeTab === "libraryCard" && !showLibraryCardTab) {
      setActiveTab("profile");
    }
  }, [activeTab, showLibraryCardTab]);

  // Event handlers
  const handlePasswordChanged = () => {
    setActiveTab("profile");
    if (onPasswordChanged) {
      onPasswordChanged();
    }
  };

  const handleImageUpdate = (newImageUrl) => {
    const updatedUser = { ...user, image_url: newImageUrl };
    setUser(updatedUser);
    if (onUserUpdate) {
      onUserUpdate(updatedUser);
    }
    if (onImageUpload) {
      onImageUpload(newImageUrl);
    }
    setImageErrors([]);
  };

  const handleProfileSubmit = async (formData) => {
    const currentImageUrl = user.image_url;

    try {
      const submissionData = {
        ...formData,
        ...(formData.library_id !== undefined && {
          library_id: formData.library_id,
        }),
      };

      await onSubmit(submissionData);

      const updatedUser = {
        ...user,
        ...submissionData,
        image_url: currentImageUrl,
      };

      setUser(updatedUser);
      setCurrentUser(updatedUser);

      if (onUserUpdate) {
        onUserUpdate(updatedUser);
      }

      const imageInfo = await profileApi.getImageInfo();
      if (imageInfo.image_url) {
        const finalUser = { ...updatedUser, image_url: imageInfo.image_url };
        setUser(finalUser);
        setCurrentUser(finalUser);
        if (onUserUpdate) {
          onUserUpdate(finalUser);
        }
      }
    } catch (error) {
      const updatedUser = {
        ...user,
        ...formData,
        image_url: currentImageUrl,
      };
      setUser(updatedUser);
      setCurrentUser(updatedUser);
      if (onUserUpdate) {
        onUserUpdate(updatedUser);
      }
      throw error;
    }
  };

  const handleImageError = (error) => {
    setImageErrors((prev) => [
      ...prev.slice(-2),
      { id: Date.now(), ...error, timestamp: new Date().toLocaleTimeString() },
    ]);
  };

  if (isLoading || fetchingUserData) {
    return <LoadingState />;
  }

  const styles = {
    section: {
      backgroundColor: COLORS.background,
      borderRadius: "8px",
      overflow: "hidden",
      marginBottom: "1.5rem",
    },
    tabContent: {
      backgroundColor: COLORS.white,
      borderRadius: "0 0 8px 8px",
      padding: isMobile ? "1rem" : "2rem",
      minHeight: isMobile ? "auto" : "400px",
      display: "flex",
      justifyContent: "center",
    },
    sectionContent: {
      backgroundColor: COLORS.background,
      borderRadius: "8px",
      overflow: "hidden",
      marginBottom: 0,
    },
    container: { maxWidth: "1200px", padding: isMobile ? "1rem" : "2rem" },
  };

  return (
    <Container className="profile-main-container" style={styles.container}>
      {imageErrors.length > 0 && (
        <Message warning style={{ marginBottom: "1.5rem" }}>
          <Message.Header>Image Upload Issues</Message.Header>
          <Message.List>
            {imageErrors.map((error) => (
              <Message.Item key={error.id}>
                {error.timestamp}: {error.message}
              </Message.Item>
            ))}
          </Message.List>
        </Message>
      )}

      {hasError && (
        <Message negative style={{ marginBottom: "1.5rem" }}>
          <Message.Header>Error Loading Profile</Message.Header>
          <div style={{ whiteSpace: "pre-line" }}>{errorMessage}</div>
        </Message>
      )}

      {hasSuccess && (
        <Message positive style={{ marginBottom: "1.5rem" }}>
          <Message.Header>Success</Message.Header>
          <p>{successMessage}</p>
        </Message>
      )}

      <div style={styles.section}>
        <SectionHeader title="Account Management" />
        <div style={{ padding: "0", backgroundColor: COLORS.white }}>
          <TabNavigation
            activeTab={activeTab}
            onTabChange={setActiveTab}
            showLibraryCard={showLibraryCardTab}
          />
          <div style={styles.tabContent}>
            {activeTab === "profile" && (
              <ProfileTabContent
                user={effectiveUser}
                isLibrarianOrAdmin={isLibrarianAdminOrFaculty}
                onSubmit={handleProfileSubmit}
                onImageUpdate={handleImageUpdate}
                onImageError={handleImageError}
              />
            )}
            {activeTab === "password" && (
              <ChangePasswordForm onPasswordChanged={handlePasswordChanged} />
            )}
            {activeTab === "libraryCard" && showLibraryCardTab && (
              <LibraryCard user={effectiveUser} />
            )}
          </div>
        </div>
      </div>

      <div style={styles.sectionContent}>
        <SectionHeader title="Account Info" fontSize="1rem" />
        <div style={{ padding: isMobile ? "1rem" : "1.5rem" }}>
          <Grid stackable>
            <Grid.Row>
              <Grid.Column width={isMobile ? 16 : 8}>
                <div style={{ marginBottom: isMobile ? "1rem" : "0" }}>
                  <strong style={{ color: COLORS.primary, fontSize: "0.9rem" }}>
                    Last Login:
                  </strong>
                  <p
                    style={{
                      margin: "0.25rem 0 0 0",
                      fontSize: "0.85rem",
                      color: COLORS.textSecondary,
                    }}
                  >
                    {(() => {
                      // Try all possible sources for last_login_at
                      const lastLogin =
                        effectiveUser?.last_login_at ||
                        user?.last_login_at ||
                        profile?.last_login_at;

                      if (!lastLogin) {
                        return "No login history";
                      }

                      try {
                        const date = new Date(lastLogin);
                        if (isNaN(date.getTime())) {
                          return "Invalid date format";
                        }
                        return date.toLocaleString();
                      } catch (e) {
                        return "Error displaying date";
                      }
                    })()}
                  </p>
                </div>
              </Grid.Column>
              <Grid.Column width={isMobile ? 16 : 8}>
                <div
                  style={{
                    padding: isMobile ? "0.8rem" : "1rem",
                    backgroundColor: COLORS.infoBackground,
                    borderRadius: "6px",
                    borderLeft: `4px solid ${COLORS.primary}`,
                  }}
                >
                  <Icon name="info circle" color="blue" />
                  <strong
                    style={{ fontSize: "0.85rem", color: COLORS.infoBorder }}
                  >
                    Privacy Notice:
                  </strong>
                  <p
                    style={{
                      margin: "0.5rem 0 0 0",
                      fontSize: "0.8rem",
                      color: COLORS.textTertiary,
                    }}
                  >
                    Your personal information is secure and will only be used
                    for academic purposes.
                  </p>
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>
      </div>
    </Container>
  );
};

ProfileAvatar.propTypes = {
  user: PropTypes.object,
  onImageUpdate: PropTypes.func.isRequired,
  onError: PropTypes.func,
};

UserInfo.propTypes = {
  user: PropTypes.object,
};

SectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
  fontSize: PropTypes.string,
};

ProfileContainer.propTypes = {
  profile: PropTypes.object.isRequired,
  user: PropTypes.object,
  isLoading: PropTypes.bool,
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
  hasSuccess: PropTypes.bool,
  successMessage: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onPasswordChanged: PropTypes.func,
  onImageUpload: PropTypes.func,
  onUserUpdate: PropTypes.func,
};

ProfileContainer.defaultProps = {
  user: {},
  isLoading: false,
  hasError: false,
  errorMessage: "",
  hasSuccess: false,
  successMessage: "",
  onPasswordChanged: null,
  onImageUpload: null,
  onUserUpdate: null,
};

export default ProfileContainer;
